import { isNil, omitBy } from 'lodash';
import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import useClientEmailQuery from 'ui/hooks/useClientEmailQuery';
import useClientPhoneQuery from 'ui/hooks/useClientPhoneQuery';
import useClientQuery from 'ui/hooks/useClientQuery';
import useDataLayerHashedValues from './useDataLayerHashedValues';
import useLoanApplicationQuery from 'ui/hooks/useLoanApplicationQuery';

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GTM_ID || '',
};

export default function useDataLayer() {
  const { data: clientData } = useClientQuery();
  const { data: clientEmail } = useClientEmailQuery();
  const { data: clientPhone } = useClientPhoneQuery();
  const { loanApplication: applicationData } = useLoanApplicationQuery();
  const prevLocation = useRef<string | null>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const {
    getHashedEmail,
    getHashedFirstName,
    getHashedLastName,
    getHashedTelephoneNumber,
  } = useDataLayerHashedValues();
  const logPageChange = async () => {
    try {
      const dataLayer: Record<string, unknown> = {
        event: 'pageChange',
        page: {
          url: window.location.pathname,
          referrer: prevLocation.current ?? document.referrer,
        },
      };
      if (clientData) {
        dataLayer.client = omitBy(
          {
            id: clientData.id,
            dateOfBirth: clientData.dateOfBirth,
            number: clientData.number,
            status: clientData.status,
            age: clientData.age,
            identifiedBy: clientData.identifiedBy,
            registeredBy: clientData.registeredBy,
            htn: await getHashedTelephoneNumber(clientPhone?.mobilePhone),
            hma: await getHashedEmail(clientEmail?.email),
            address: omitBy(
              {
                hfn: await getHashedFirstName(clientData.firstName),
                hln: await getHashedLastName(clientData.lastName),
                city: clientData.declaredAddress?.location1,
                'postal-code': clientData.declaredAddress?.postalCode,
              },
              isNil,
            ),
          },
          isNil,
        );
      }
      if (applicationData && applicationData.status === 'OPEN') {
        dataLayer.application = omitBy(
          {
            id: applicationData.id,
            amount: applicationData.amount,
            term: applicationData.term,
          },
          isNil,
        );
      }

      TagManager.dataLayer({
        dataLayer,
      });
      prevLocation.current = window.location.pathname;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error logging page change do GTM dataLayer', e);
    }
  };
  const debouncedLogPageChange = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(logPageChange, 2000);
  };
  useEffect(() => {
    TagManager.initialize(tagManagerArgs);
    // Track basic JavaScript errors
    window.addEventListener('error', function (e) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'error',
          message: e.message,
          stacktrace: e.error.stack,
          path: window.location.pathname,
        },
      });
    });
    if (process.env.REACT_APP_GTM) {
      // Log the initial page load
      logPageChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (process.env.REACT_APP_GTM) {
      debouncedLogPageChange();
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [clientData, applicationData]);
}
